import React, { useCallback, useEffect, useRef, useState } from 'react';
import { resolveGeneratedImage } from '../utils/generatedImageCache';
import { ChatResponseFile } from '../../Chat/types';
import './GeneratedImage.css';

interface GeneratedImageProps {
  file: ChatResponseFile;
  userId: string;
  onOpenPreview: (file: ChatResponseFile, imageUrl: string) => void;
}

// Caps silent auto-reload attempts (see handleImgError below) so a
// genuinely broken/missing image settles into the error state instead of
// retrying forever.
const MAX_AUTO_RETRIES = 2;

// Renders a bounded thumbnail for a generated image, resolved via the
// shared generatedImageCache (fetched once per file_id, reused as a local
// blob: URL for every subsequent display — see that module for why). The
// full-size preview itself is owned by a single shared ImagePreviewModal
// (rendered at the Chat root) rather than one per thumbnail, so it isn't
// nested inside a hoverable/transformed message bubble ancestor — see
// ImagePreviewModal.tsx for why that matters.
const GeneratedImage: React.FC<GeneratedImageProps> = ({ file, userId, onOpenPreview }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const cancelledRef = useRef(false);
  const retryCountRef = useRef(0);

  const loadImage = useCallback(() => {
    cancelledRef.current = false;
    setIsLoading(true);
    setHasError(false);

    resolveGeneratedImage(file, userId)
      .then((url) => {
        if (!cancelledRef.current) setImageUrl(url);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        console.warn('[GeneratedImage] Could not load image:', file.path, err);
        setHasError(true);
      })
      .finally(() => {
        if (!cancelledRef.current) setIsLoading(false);
      });
    // `file` comes from message.files, which stays referentially stable
    // across re-renders of the same message, so depending on the object
    // itself (as resolveGeneratedImage's signature expects) is safe here.
  }, [file, userId]);

  useEffect(() => {
    retryCountRef.current = 0;
    loadImage();
    return () => {
      cancelledRef.current = true;
    };
  }, [loadImage]);

  // A blob: URL that rendered fine once can still break later — most
  // commonly generatedImageCache evicting it (LRU cap), or the CORS
  // fallback path handing out a raw SAS URL that Azure rejects on a
  // second identical request (see generatedImageCache.ts). Silently
  // re-resolve and swap in a fresh URL rather than leaving a broken
  // thumbnail on screen.
  const handleImgError = () => {
    if (retryCountRef.current >= MAX_AUTO_RETRIES) {
      console.warn(`[GeneratedImage] Image still broken after ${MAX_AUTO_RETRIES} reload(s):`, file.path);
      setHasError(true);
      return;
    }
    retryCountRef.current += 1;
    loadImage();
  };

  return (
    <div className="generated-image-box">
      {isLoading && (
        <div className="generated-image-status">
          <span className="generated-image-spinner" aria-hidden="true" />
          <span>Loading image…</span>
        </div>
      )}

      {!isLoading && hasError && (
        <div className="generated-image-status generated-image-status--error">
          <i className="fas fa-exclamation-triangle" aria-hidden="true" />
          <span>Couldn't load image</span>
        </div>
      )}

      {!isLoading && !hasError && imageUrl && (
        <button
          type="button"
          className="generated-image-thumb-btn"
          onClick={() => onOpenPreview(file, imageUrl)}
          aria-label="View generated image"
        >
          <img
            src={imageUrl}
            alt="Generated"
            className="generated-image-thumb"
            onError={handleImgError}
          />
        </button>
      )}
    </div>
  );
};

export default GeneratedImage;
